import { type ReactNode } from "react";

import { Inter } from "next/font/google";

import { cn } from "@/shared/helpers/cn.helper";

const familyMainFont = Inter({ subsets: ["latin"], weight: ["400", "500", "600", "700"] });

const fonts ={
  familyMainFont,
};

const textColorsClasses = {
  50: "text-[#FFFFFF]",
  100: "text-[#CED4DA]",
  200: "text-[#ADB5BD]",
  300: "text-text-tertiary",
  400: "text-text-secondary",
  500: "text-text-primary",
} as const;

type TextColorShade = keyof typeof textColorsClasses;

export interface TypographyProps {
  readonly children?: ReactNode;
  readonly variant?: number;
  readonly className?: string;
  readonly colorShade?: TextColorShade;
  readonly dangerouslySetInnerHTML?: { __html: string };
  readonly capitalize?: boolean;
  readonly uppercase?: boolean;
}

const textTransformModifiers = {
  capitalize: "capitalize after:capitalize",
  uppercase: "uppercase after:uppercase",
};

const typographyClasses = {
  Title: {
    default: `${fonts.familyMainFont.className} tracking-[0px] after:tracking-[0px] after:font-[var(--font-inter)]`,
    classnames: {
      h1: "text-[42px]/[50px] font-bold",
      h2: "text-[32px]/[42px] font-semibold",
      h3: "text-[28px]/[38px] font-semibold",
      h4: "text-[22px]/[32px] font-semibold",
      h5: "text-[18px]/[28px] font-semibold",
      title: "text-[16px]/[24px] font-bold",
      subTitle: "text-[14px]/[20px] font-semibold",
      tableTitleS: "text-[12px]/[20px] font-medium tracking-[1px] after:text-[12px]/[20px] after:font-medium after:tracking-[1px]",
      tableTitleXS: "text-[10px]/[16px] font-medium tracking-[1px]",
    },
  },
  Body:{
    default: `${fonts.familyMainFont.className} font-medium tracking-[0px] normal-case`,
    classnames: {
      l: "text-[16px]/[24px]",
      mMedium: "text-[14px]/[20px]",
      mRegular: "text-[14px]/[20px] font-normal",
      sMedium: "text-[12px]/[16px]",
      sRegular: "text-[12px]/[16px] font-normal",
      xs: "text-[10px]/[12px]",
    },
  },
  label: {
    default: `${fonts.familyMainFont.className} tracking-[0px] normal-case`,
    classnames: {
      label: "text-[14px]/[20px] font-bold",
    },
  },
};


function Typography({ children, className }: TypographyProps) {
  return <div className={className}>{children}</div>;
}

function H1({
  children,
  colorShade = 500,
  className = "",
  dangerouslySetInnerHTML,
  capitalize = true,
  uppercase,
}: TypographyProps) {
  return (
    <h1
      className={cn(
        typographyClasses.Title.default,
        typographyClasses.Title.classnames.h1,
        textColorsClasses[colorShade],
        capitalize && textTransformModifiers.capitalize,
        uppercase && textTransformModifiers.uppercase,
        className,
      )}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
    >
      {children}
    </h1>
  );
}

function H2({
  children,
  colorShade = 500,
  className = "",
  dangerouslySetInnerHTML,
  capitalize = true,
  uppercase,
}: TypographyProps) {
  return (
    <h2
      className={cn(
        typographyClasses.Title.default,
        typographyClasses.Title.classnames.h2,
        textColorsClasses[colorShade],
        capitalize && textTransformModifiers.capitalize,
        uppercase && textTransformModifiers.uppercase,
        className,
      )}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
    >
      {children}
    </h2>
  );
}

function H3({
  children,
  colorShade = 500,
  className = "",
  dangerouslySetInnerHTML,
  capitalize = true,
  uppercase,
}: TypographyProps) {
  return (
    <h3
      className={cn(
        typographyClasses.Title.default,
        typographyClasses.Title.classnames.h3,
        textColorsClasses[colorShade],
        capitalize && textTransformModifiers.capitalize,
        uppercase && textTransformModifiers.uppercase,
        className,
      )}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
    >
      {children}
    </h3>
  );
}

function H4({
  children,
  colorShade = 500,
  className = "",
  dangerouslySetInnerHTML,
  capitalize = true,
  uppercase,
}: TypographyProps) {
  return (
    <h4
      className={cn(
        typographyClasses.Title.default,
        typographyClasses.Title.classnames.h4,
        textColorsClasses[colorShade],
        capitalize && textTransformModifiers.capitalize,
        uppercase && textTransformModifiers.uppercase,
        className,
      )}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
    >
      {children}
    </h4>
  );
}

function H5({
  children,
  colorShade = 500,
  className = "",
  dangerouslySetInnerHTML,
  capitalize = true,
  uppercase,
}: TypographyProps) {
  return (
    <h5
      className={cn(
        typographyClasses.Title.default,
        typographyClasses.Title.classnames.h5,
        textColorsClasses[colorShade],
        capitalize && textTransformModifiers.capitalize,
        uppercase && textTransformModifiers.uppercase,
        className,
      )}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
    >
      {children}
    </h5>
  );
}

function Title({
  children,
  colorShade = 500,
  className = "",
  dangerouslySetInnerHTML,
  capitalize = true,
  uppercase,
}: TypographyProps) {
  return (
    <p
      className={cn(
        typographyClasses.Title.default,
        typographyClasses.Title.classnames.title,
        textColorsClasses[colorShade],
        capitalize && textTransformModifiers.capitalize,
        uppercase && textTransformModifiers.uppercase,
        className,
      )}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
    >
      {children}
    </p>
  );
}

function SubTitle({
  children,
  colorShade = 500,
  className = "",
  dangerouslySetInnerHTML,
  capitalize = true,
  uppercase,
}: TypographyProps) {
  return (
    <p
      className={cn(
        typographyClasses.Title.default,
        typographyClasses.Title.classnames.subTitle,
        textColorsClasses[colorShade],
        capitalize && textTransformModifiers.capitalize,
        uppercase && textTransformModifiers.uppercase,
        className,
      )}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
    >
      {children}
    </p>
  );
}

function TableTitleS({
  children,
  colorShade = 500,
  className = "",
  dangerouslySetInnerHTML,
  capitalize = true,
  uppercase = true,
}: TypographyProps) {
  return (
    <p
      className={cn(
        typographyClasses.Title.default,
        typographyClasses.Title.classnames.tableTitleS,
        textColorsClasses[colorShade],
        capitalize && textTransformModifiers.capitalize,
        uppercase && textTransformModifiers.uppercase,
        className,
      )}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
    >
      {children}
    </p>
  );
}

function TableTitleXS({
  children,
  colorShade = 500,
  className = "",
  dangerouslySetInnerHTML,
  capitalize = true,
  uppercase = true,
}: TypographyProps) {
  return (
    <p
      className={cn(
        typographyClasses.Title.default,
        typographyClasses.Title.classnames.tableTitleXS,
        textColorsClasses[colorShade],
        capitalize && textTransformModifiers.capitalize,
        uppercase && textTransformModifiers.uppercase,
        className,
      )}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
    >
      {children}
    </p>
  );
}

function BodyL({
  children,
  colorShade = 500,
  className = "",
  dangerouslySetInnerHTML,
  capitalize = true,
  uppercase,
}: TypographyProps) {
  return (
    <p
      className={cn(
        typographyClasses.Body.default,
        typographyClasses.Body.classnames.l,
        textColorsClasses[colorShade],
        capitalize && textTransformModifiers.capitalize,
        uppercase && textTransformModifiers.uppercase,
        className,
      )}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
    >
      {children}
    </p>
  );
}

function BodyMMedium({
  children,
  colorShade = 500,
  className = "",
  dangerouslySetInnerHTML,
  capitalize = true,
  uppercase,
}: TypographyProps) {
  return (
    <p
      className={cn(
        typographyClasses.Body.default,
        typographyClasses.Body.classnames.mMedium,
        textColorsClasses[colorShade],
        capitalize && textTransformModifiers.capitalize,
        uppercase && textTransformModifiers.uppercase,
        className,
      )}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
    >
      {children}
    </p>
  );
}

function BodyMRegular({
  children,
  colorShade = 500,
  className = "",
  dangerouslySetInnerHTML,
  capitalize = true,
  uppercase,
}: TypographyProps) {
  return (
    <p
      className={cn(
        typographyClasses.Body.default,
        typographyClasses.Body.classnames.mRegular,
        textColorsClasses[colorShade],
        capitalize && textTransformModifiers.capitalize,
        uppercase && textTransformModifiers.uppercase,
        className,
      )}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
    >
      {children}
    </p>
  );
}

function BodySMedium({
  children,
  colorShade = 500,
  className = "",
  dangerouslySetInnerHTML,
  capitalize = true,
  uppercase,
}: TypographyProps) {
  return (
    <p
      className={cn(
        typographyClasses.Body.default,
        typographyClasses.Body.classnames.sMedium,
        textColorsClasses[colorShade],
        capitalize && textTransformModifiers.capitalize,
        uppercase && textTransformModifiers.uppercase,
        className,
      )}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
    >
      {children}
    </p>
  );
}

function BodySRegular({
  children,
  colorShade = 500,
  className = "",
  dangerouslySetInnerHTML,
  capitalize = true,
  uppercase,
}: TypographyProps) {
  return (
    <p
      className={cn(
        typographyClasses.Body.default,
        typographyClasses.Body.classnames.sRegular,
        textColorsClasses[colorShade],
        capitalize && textTransformModifiers.capitalize,
        uppercase && textTransformModifiers.uppercase,
        className,
      )}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
    >
      {children}
    </p>
  );
}

function BodyXS({
  children,
  colorShade = 500,
  className = "",
  dangerouslySetInnerHTML,
  capitalize = true,
  uppercase,
}: TypographyProps) {
  return (
    <p
      className={cn(
        typographyClasses.Body.default,
        typographyClasses.Body.classnames.xs,
        textColorsClasses[colorShade],
        capitalize && textTransformModifiers.capitalize,
        uppercase && textTransformModifiers.uppercase,
        className,
      )}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
    >
      {children}
    </p>
  );
}

function Label({
  children,
  colorShade = 500,
  className = "",
  dangerouslySetInnerHTML,
  capitalize = true,
  uppercase,
}: TypographyProps) {
  return (
    <label
      className={cn(
        typographyClasses.label.default,
        typographyClasses.label.classnames.label,
        textColorsClasses[colorShade],
        capitalize && textTransformModifiers.capitalize,
        uppercase && textTransformModifiers.uppercase,
        className,
      )}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
    >
      {children}
    </label>
  );
}

Typography.H1 = H1;
Typography.H2 = H2;
Typography.H3 = H3;
Typography.H4 = H4;
Typography.H5 = H5;
Typography.Title = Title;
Typography.SubTitle = SubTitle;
Typography.TableTitleS = TableTitleS;
Typography.TableTitleXS = TableTitleXS;
Typography.BodyL = BodyL;
Typography.BodyMMedium = BodyMMedium;
Typography.BodyMRegular = BodyMRegular;
Typography.BodySMedium = BodySMedium;
Typography.BodySRegular = BodySRegular;
Typography.BodyXS = BodyXS;
Typography.Label = Label;

Typography.classes = typographyClasses;
Typography.textTransformModifiers = textTransformModifiers;

export default Typography;
